import { PraksisavtaleStedDtoKapasitetstypeDtoEnum } from '../../api/generated/praksissted-generated-api';
import { GenericActionWithPayload } from '../../util';
import {
  PraksisaktivitetAvtalerDto,
  PraksisavtaleStedBestillingspunktTypeDto,
  PraksisavtalestedValideringfeilDto,
  PraksisavtaleStedDto,
} from '../../api/generated/studiested-generated-api';

export type StedAvtaleBestilling = {
  bestilling: number | undefined;
  kapasitet: number | undefined;
  kapasitetstype: PraksisavtaleStedDtoKapasitetstypeDtoEnum | undefined;
  kapasitetspunkt: boolean;
  deaktivert: boolean;
  deaktivertAvForelderKapasitet: boolean;
  hasDeaktivertForelder: boolean;
  hasSkjultForelder: boolean;
  skjult: boolean;
  valideringsfeil: PraksisavtalestedValideringfeilDto | undefined;
};

export type StedAvtaleBestillingPayload = {
  [avtaleKey: string]: {
    bestilling?: number;
    kapasitet?: number;
    kapasitetstype?: PraksisavtaleStedDtoKapasitetstypeDtoEnum;
    kapasitetspunkt?: boolean;
    deaktivert?: boolean;
    deaktivertAvForelderKapasitet?: boolean;
    skjult?: boolean;
    hasDeaktivertForelder?: boolean;
    hasSkjultForelder?: boolean;
  };
};

export type StedAvtaleBestillinger = {
  [avtaleKey: string]: StedAvtaleBestilling;
};

export enum AgreementsActionType {
  UPDATE,
}

export type AgreementsAction = GenericActionWithPayload<
  AgreementsActionType,
  StedAvtaleBestillingPayload
>;

export type StedWithUndernoderAndBestillingspunkt = {
  bestillingspunkt?: PraksisavtaleStedBestillingspunktTypeDto;
  undernoder: PraksisavtaleStedDto[];
};

export type Expanded = { [avtaleKey: string]: boolean };

export type NearestAvtaleKeyParent = {
  [avtaleKey: string]: string;
};

export type ApplyToAllUndernoderKey = keyof StedAvtaleBestilling;
export type AppliedToAllUndernoderForStedAvtaleBestilling = {
  [avtaleKey: string]: { [key: string]: unknown };
};

/* Add fields, when needed */
export type FlattenedAvtaleSted = {
  bestilling: number | undefined;
  kapasitet: number | undefined;
  kapasitetspunkt: boolean;
  kapasitetstype: PraksisavtaleStedDtoKapasitetstypeDtoEnum | undefined;
  bestillingspunkt: PraksisavtaleStedBestillingspunktTypeDto | undefined;
  overnoder: string[];
  overnoderOgDeresSosken: string[];
  soskennoder: string[];
  undernoder: PraksisavtaleStedDto[];
  navn: string;
  valideringsfeil: PraksisavtalestedValideringfeilDto[] | undefined;
};

export type FlattenedAvtaleSteder = {
  [avtaleKey: string]: FlattenedAvtaleSted;
};

export interface PraksisavtaleSted extends PraksisavtaleStedDto {
  topNode: boolean;
  bestillingIsAtTop: boolean;
  avtaleId: string;
  avtalenavn: string | undefined;
  indent: number;
  periode?: string;
}

export interface HandlingsknapperProps {
  delt: boolean;
  kanEndre: boolean;
  activityChanged: boolean;
  isAllowedToShare: boolean;
  onSave: () => void;
  onShare: () => void;
}

export interface ConfirmBestillingerProps {
  onClose: (confirm: boolean) => void;
  isOpen: boolean;
  avtalestederUtenAdmin: string[];
}

export enum AvtaleTableHeader {
  PRAKSISSTED = 'PRAKSISSTED',
  AVTALE = 'AVTALE',
  STATUS = 'STATUS',
  BESTILLING = 'BESTILLING',
  KAPASITET = 'KAPASITET',
  HANDLINGER = 'HANDLINGER',
}

export type AvtaleTableHeaderType = {
  [headerType in AvtaleTableHeader]: string;
};

export type Bestillingsmoduser = {
  [id: string]: PraksisavtaleStedBestillingspunktTypeDto;
};

export interface AgreementTableContextProps {
  avtaler: PraksisaktivitetAvtalerDto[] | undefined;
  shouldDisplayPraksissted: (
    agreementRow: PraksisavtaleSted,
    tableSearch: string,
  ) => boolean;
  expanded: Expanded;
  toggleExpanded: (avtaleId: string, bestillingIsAtTop: boolean) => void;
  onBestillingChange: (
    avtaleId: string,
    praksisstedId: string,
    bestilling: number | undefined,
  ) => void;
  onKapasitetChange: (
    avtaleId: string,
    praksisstedId: string,
    kapasitet: number | undefined,
  ) => void;
  kanEndre: boolean;
  delt: boolean;
  handleDeaktivertPraksissted: (key: string) => void;
  handleSkjultPraksissted: (key: string) => void;
  bestillingsmoduser: Bestillingsmoduser;
  toggleBestillingsmodus: (avtaleId: string) => void;
  flyttKapasitetspunktTil: (avtaleKey: string) => void;
  kanFlytteKapasitetTilSteder: string[]; // AvtaleKeys
  addAvtalestedWithoutAdmin: (
    praksisstedNavn: string,
    value: number | undefined,
  ) => void;
  kanFlytteBestillinger: string[]; // AvtaleIds
  praksisaktivitetId: string;
  state: StedAvtaleBestillinger;
  haveOvernodeOrSiblingOrUndernodeWithFordeling: string[]; // AvtaleKeys
  haveOvernodeOrSiblingOrUndernodeWithOnske: string[]; // AvtaleKeys
}

export interface AgreementRowProps {
  initSted: PraksisavtaleSted;
}

interface TableRowState extends StedAvtaleBestilling {
  isExpanded: boolean;
  bestillingsmodus: PraksisavtaleStedBestillingspunktTypeDto;
}

export interface AgreementTableRowProps extends AgreementRowProps {
  avtaleKey: string;
  headers: { [key in AvtaleTableHeader]: string };
  rowState: TableRowState;
}

export interface FlyttKapasitetDialogProps {
  isOpen: boolean;
  onClose: (confirmed: boolean) => void;
  flyttTil: string;
  delt: boolean;
  hasOvernodeOrSiblingOrUndernodeWithFordeling: boolean;
  hasOvernodeOrSiblingOrUndernodeWithOnske: boolean;
  undernoderMedKapasitetNavn: string[];
}

export enum SubmitButton {
  Save,
  Share,
}

export enum Direction {
  UP,
  DOWN,
}
